'use client';

import { useState, useEffect } from 'react';
import { DebugImageOverlay } from './DebugImageOverlay';
import { useToast } from '@/components/ui/use-toast';

interface DebugOverlayState {
  isOpen: boolean;
  imageName: string;
  imageUrl: string;
  isLoading: boolean;
  error: string | null;
}

export function DebugOverlayProvider() {
  const { toast } = useToast();
  const [overlay, setOverlay] = useState<DebugOverlayState>({
    isOpen: false,
    imageName: '',
    imageUrl: '',
    isLoading: false,
    error: null
  });

  useEffect(() => {
    // Move envVars definition inside useEffect
    const envVars = {
      DEBUG_MODE: process.env.NEXT_PUBLIC_DEBUG_MODE,
      MCP_SERVER: process.env.NEXT_PUBLIC_MCP_SERVER,
      API_SERVER: process.env.NEXT_PUBLIC_API_SERVER,
      IMAGE_SERVICE: process.env.NEXT_PUBLIC_IMAGE_SERVICE || 'mcp'
    };
    
    // Log environment variables for debugging
    console.log('Debug environment variables:', envVars);
    
    // Listen for the custom event to open the debug overlay
    const handleOpenDebugOverlay = (event: CustomEvent<{ imageName: string; imageUrl: string }>) => {
      try {
        console.log('Opening debug overlay for:', event.detail);
        
        // Show loading state
        setOverlay({
          isOpen: true,
          imageName: event.detail.imageName || 'unknown',
          imageUrl: event.detail.imageUrl || '',
          isLoading: true,
          error: null
        });
        
        // Simulate successful loading after a short delay
        // This ensures the overlay opens even if there's an issue with the MCP server
        setTimeout(() => {
          setOverlay(prev => ({
            ...prev,
            isLoading: false
          }));
        }, 500);
        
        // Show toast notification
        toast({
          title: 'Debug Overlay',
          description: `Opening debug overlay for ${event.detail.imageName || 'unknown'}`,
        });
      } catch (error) {
        console.error('Error opening debug overlay:', error);
        toast({
          title: 'Error',
          description: 'Failed to open debug overlay',
          variant: 'destructive',
        });
      }
    };

    // Listen for the custom event to close the debug overlay
    const handleCloseDebugOverlay = () => {
      setOverlay(prev => ({ ...prev, isOpen: false }));
    };

    // Add event listeners
    window.addEventListener('openDebugOverlay', handleOpenDebugOverlay as EventListener);
    window.addEventListener('closeDebugOverlay', handleCloseDebugOverlay);

    // Clean up
    return () => {
      window.removeEventListener('openDebugOverlay', handleOpenDebugOverlay as EventListener);
      window.removeEventListener('closeDebugOverlay', handleCloseDebugOverlay);
    };
  }, [toast]);

  // If the overlay is not open, don't render anything
  if (!overlay.isOpen) {
    return null;
  }

  // Render the debug overlay
  return (
    <div className="fixed inset-0 z-50">
      <DebugImageOverlay
        imageName={overlay.imageName}
        imageUrl={overlay.imageUrl}
      />
    </div>
  );
} 